import type { V2 } from '../core/math';
import type { CityLayout, RoadEdge } from './types';

/** Helpers over the road graph. Pure functions — safe for any module to use. */

export function edgeDirection(layout: CityLayout, edge: RoadEdge): V2 {
  const a = layout.roads.nodes[edge.from]!.pos;
  const b = layout.roads.nodes[edge.to]!.pos;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const l = Math.sqrt(dx * dx + dz * dz) || 1;
  return { x: dx / l, z: dz / l };
}

export function edgeLength(layout: CityLayout, edge: RoadEdge): number {
  const a = layout.roads.nodes[edge.from]!.pos;
  const b = layout.roads.nodes[edge.to]!.pos;
  return Math.hypot(b.x - a.x, b.z - a.z);
}

/**
 * Signed lateral offset (metres, positive = LEFT of the from->to direction) of a lane centre.
 * Left-hand traffic: forward lanes sit left of the centre line, backward lanes right of it.
 * One-way roads centre their lanes on the edge centre line.
 */
export function laneOffset(edge: RoadEdge, forward: boolean, lane: number): number {
  if (edge.oneWay) {
    const n = edge.lanesForward;
    return (lane + 0.5 - n / 2) * edge.laneWidth;
  }
  const off = (lane + 0.5) * edge.laneWidth;
  return forward ? off : -off;
}

/**
 * World position on a lane: `t` in [0,1] along the travel direction
 * (forward: from->to; backward: to->from).
 */
export function lanePoint(layout: CityLayout, edge: RoadEdge, forward: boolean, lane: number, t: number): V2 {
  const a = layout.roads.nodes[edge.from]!.pos;
  const b = layout.roads.nodes[edge.to]!.pos;
  const d = edgeDirection(layout, edge);
  // left of from->to direction = (d.z, -d.x)... derived from left = -right, right(h) = (-cos h, sin h)
  const left = { x: d.z, z: -d.x };
  const off = laneOffset(edge, forward, lane);
  const s = forward ? t : 1 - t;
  return {
    x: a.x + (b.x - a.x) * s + left.x * off,
    z: a.z + (b.z - a.z) * s + left.z * off,
  };
}

/** Edges leaving node `nodeId` that can be driven away from it, with their travel direction. */
export function outgoing(layout: CityLayout, nodeId: number): { edge: RoadEdge; forward: boolean }[] {
  const out: { edge: RoadEdge; forward: boolean }[] = [];
  for (const e of layout.roads.edges) {
    if (e.from === nodeId && e.lanesForward > 0) out.push({ edge: e, forward: true });
    else if (e.to === nodeId && !e.oneWay && e.lanesBackward > 0) out.push({ edge: e, forward: false });
  }
  return out;
}

/** Closest point on any edge centre line to p. */
export function nearestEdge(layout: CityLayout, p: V2): { edge: RoadEdge; t: number; distance: number } | null {
  let best: { edge: RoadEdge; t: number; distance: number } | null = null;
  for (const e of layout.roads.edges) {
    const a = layout.roads.nodes[e.from]!.pos;
    const b = layout.roads.nodes[e.to]!.pos;
    const abx = b.x - a.x;
    const abz = b.z - a.z;
    const l2 = abx * abx + abz * abz || 1;
    let t = ((p.x - a.x) * abx + (p.z - a.z) * abz) / l2;
    t = Math.max(0, Math.min(1, t));
    const cx = a.x + abx * t;
    const cz = a.z + abz * t;
    const d = Math.hypot(p.x - cx, p.z - cz);
    if (!best || d < best.distance) best = { edge: e, t, distance: d };
  }
  return best;
}

/**
 * Heading (rad, forward = (sin h, cos h)) of the road under `p`, or null when `p` is off the
 * carriageway or inside a junction box. Either direction along the road is the same axis. Used by the
 * touch steering's road alignment (src/input/touch.ts `alignToRoad`).
 */
export function roadAxisAt(layout: CityLayout, p: V2): number | null {
  const near = nearestEdge(layout, p);
  if (!near || near.distance > near.edge.width / 2 + 0.5) return null;
  for (const it of layout.intersections) {
    if (Math.hypot(p.x - it.pos.x, p.z - it.pos.z) < it.radius + 2) return null;
  }
  const d = edgeDirection(layout, near.edge);
  return Math.atan2(d.x, d.z);
}
